/**
 * AP-8 — FM-WIN-07: Node Scan (CANopen).
 * Portierung des Zustandsautomaten aus NodeScanWindow.cpp: je Knoten-ID 1..127
 * ein SDO-Upload auf 0x1000 (Praesenz — JEDE Antwort, auch ein Abort, gilt als
 * "Knoten vorhanden"), danach die optionalen Strings 0x1008/0x1009/0x100A.
 *
 * Abweichung: die Antworten kommen aus dem `rx-batch` des Workers und
 * durchlaufen damit den Anzeigefilter. `store.openNodeScan()` stellt die
 * ID-Breite bereits auf 11 Bit; ein aktiver Masken-/Bereichsfilter kann
 * SDO-Antworten dennoch ausblenden (im Original umgeht OnSdoRawFrame den
 * Filter). Vermerkt in PROGRESS.md fuer AP-14.
 */

import './viewsB.css';
import { createDataTable } from './dataTable';
import { createFloatingPanel } from './filters/FloatingPanel';
import { createButton } from '../ui/controls';
import { FRAME_FLAG, frameCount, readFrameRecord } from '../worker/protocol';
import type { AppStore } from '../app/store';
import type { CanConnection } from '../app/connection';

const TICK_INTERVAL_MS = 10;
const RESPONSE_TIMEOUT_MS = 60;
const MAX_ATTEMPTS = 3;
const MAX_NODE_ID = 127;

const INDEX_DEVICE_TYPE = 0x1000;
const INDEX_DEVICE_NAME = 0x1008;
const INDEX_HW_VERSION = 0x1009;
const INDEX_SW_VERSION = 0x100a;

type Phase = 'presence' | 'deviceName' | 'hwVersion' | 'swVersion';

interface FoundNode {
  nodeId: number;
  deviceName: string;
  hwVersion: string;
  swVersion: string;
}

function emptyNode(): FoundNode {
  return { nodeId: 0, deviceName: '', hwVersion: '', swVersion: '' };
}

/** `BuildSdoUploadRequest()` — 0x600+node, ccs=2 (Initiate Upload). */
function uploadRequest(nodeId: number, index: number): { id: number; dlc: number; data: Uint8Array } {
  const data = new Uint8Array(8);
  data[0] = 0x40;
  data[1] = index & 0xff;
  data[2] = (index >> 8) & 0xff;
  data[3] = 0;
  return { id: 0x600 + nodeId, dlc: 8, data };
}

/** `BuildUploadSegmentRequest()` — ccs=3 plus Toggle-Bit. */
function segmentRequest(nodeId: number, toggle: boolean): { id: number; dlc: number; data: Uint8Array } {
  const data = new Uint8Array(8);
  data[0] = 0x60 | (toggle ? 0x10 : 0x00);
  return { id: 0x600 + nodeId, dlc: 8, data };
}

/**
 * `StoreIdentityResult()`: manche SDO-Server fuellen nicht genutzte Bytes
 * nicht mit 0 — ab dem ersten NUL abschneiden und alles nicht Druckbare am
 * Ende entfernen, sonst erscheinen Ersatzzeichen in der Liste.
 */
function cleanIdentityString(bytes: readonly number[]): string {
  const end = bytes.indexOf(0);
  const used = end >= 0 ? bytes.slice(0, end) : bytes.slice();
  while (used.length > 0) {
    const last = used[used.length - 1] ?? 0;
    if (last >= 0x20 && last <= 0x7e) break;
    used.pop();
  }
  return String.fromCharCode(...used);
}

export function createNodeScanView(store: AppStore, connection: CanConnection): { show(): void } {
  const panel = createFloatingPanel('Node Scan');
  panel.element.classList.add('cm-panel-window--wide');

  const root = document.createElement('div');
  root.className = 'cm-viewb';

  const controls = document.createElement('div');
  controls.className = 'cm-viewb__row';
  const startStopBtn = createButton('Start Scan', { accent: true, onClick: () => toggleScan() });
  const clearBtn = createButton('Clear Scan', { onClick: () => clearScan() });
  controls.append(startStopBtn, clearBtn);

  const status = document.createElement('div');
  status.className = 'cm-viewb__status';
  status.textContent = 'Idle.';

  const table = createDataTable({
    columns: ['Node ID', 'Device Name', 'HW Version', 'SW Version'],
    emptyText: 'no nodes found',
  });

  root.append(controls, status, table.element);
  panel.body.append(root);

  // -- Zustandsautomat ------------------------------------------------------
  let scanning = false;
  let currentNodeId = 0;
  let currentPhase: Phase = 'presence';
  let pendingIndex = INDEX_DEVICE_TYPE;
  let attemptsLeft = MAX_ATTEMPTS;
  let requestSentAt = 0;
  let awaitingSegment = false;
  let toggleBit = false;
  let segmentBuffer: number[] = [];
  let currentNode = emptyNode();
  let foundCount = 0;
  let lastStopReason = '';
  const found: FoundNode[] = [];
  let tickTimer: ReturnType<typeof setInterval> | null = null;

  function renderRows(): void {
    table.setRows(
      found.map((node) => [
        String(node.nodeId),
        node.deviceName === '' ? '-' : node.deviceName,
        node.hwVersion === '' ? '-' : node.hwVersion,
        node.swVersion === '' ? '-' : node.swVersion,
      ]),
    );
  }

  function updateStatus(): void {
    if (scanning) {
      status.textContent = `Scanning node ${currentNodeId}/${MAX_NODE_ID}... (${foundCount} found)`;
    } else if (lastStopReason !== '') {
      status.textContent = `Scan finished (${lastStopReason}). ${foundCount} node(s) found.`;
    } else {
      status.textContent = 'Idle.';
    }
  }

  function send(frame: { id: number; dlc: number; data: Uint8Array }): void {
    void connection.sendFrame({ ...frame, extended: false, remote: false }).catch(() => {
      stopScan('send failed');
    });
    requestSentAt = performance.now();
  }

  function sendPresenceProbe(): void {
    currentPhase = 'presence';
    pendingIndex = INDEX_DEVICE_TYPE;
    awaitingSegment = false;
    send(uploadRequest(currentNodeId, INDEX_DEVICE_TYPE));
  }

  function sendIdentityRequest(index: number): void {
    pendingIndex = index;
    awaitingSegment = false;
    send(uploadRequest(currentNodeId, index));
  }

  function storeIdentityResult(bytes: readonly number[]): void {
    const text = cleanIdentityString(bytes);
    if (currentPhase === 'deviceName') currentNode.deviceName = text;
    else if (currentPhase === 'hwVersion') currentNode.hwVersion = text;
    else if (currentPhase === 'swVersion') currentNode.swVersion = text;
  }

  function moveToNextNode(): void {
    currentNodeId += 1;
    if (currentNodeId > MAX_NODE_ID) {
      stopScan('scan complete');
      return;
    }
    currentNode = emptyNode();
    attemptsLeft = MAX_ATTEMPTS;
    sendPresenceProbe();
    updateStatus();
  }

  function advancePhaseAfterResult(): void {
    switch (currentPhase) {
      case 'presence':
        currentNode.nodeId = currentNodeId;
        currentPhase = 'deviceName';
        attemptsLeft = MAX_ATTEMPTS;
        sendIdentityRequest(INDEX_DEVICE_NAME);
        updateStatus();
        return;
      case 'deviceName':
        currentPhase = 'hwVersion';
        attemptsLeft = MAX_ATTEMPTS;
        sendIdentityRequest(INDEX_HW_VERSION);
        return;
      case 'hwVersion':
        currentPhase = 'swVersion';
        attemptsLeft = MAX_ATTEMPTS;
        sendIdentityRequest(INDEX_SW_VERSION);
        return;
      case 'swVersion':
        found.push({ ...currentNode });
        foundCount += 1;
        renderRows();
        moveToNextNode();
        return;
    }
  }

  function onTick(): void {
    if (!scanning) return;
    if (performance.now() - requestSentAt < RESPONSE_TIMEOUT_MS) return;

    // Verstummt ein Server mitten im segmentierten Transfer, wird das
    // bereits Gesammelte uebernommen — kein Neuversuch (NodeScanWindow.cpp:378).
    if (awaitingSegment) {
      awaitingSegment = false;
      storeIdentityResult(segmentBuffer);
      segmentBuffer = [];
      advancePhaseAfterResult();
      return;
    }

    attemptsLeft -= 1;
    if (attemptsLeft > 0) {
      if (currentPhase === 'presence') sendPresenceProbe();
      else sendIdentityRequest(pendingIndex);
      return;
    }

    if (currentPhase === 'presence') moveToNextNode();
    else advancePhaseAfterResult();
  }

  function onSdoResponse(nodeId: number, dlc: number, data: Uint8Array): void {
    if (!scanning || dlc < 1) return;
    if (nodeId !== currentNodeId) return;
    const cs = data[0] ?? 0;

    if (awaitingSegment) {
      if (cs === 0x80) {
        if (dlc < 8) return;
        const respIndex = (data[1] ?? 0) | ((data[2] ?? 0) << 8);
        if (respIndex !== pendingIndex || (data[3] ?? 0) !== 0) return;
        awaitingSegment = false;
        advancePhaseAfterResult();
        return;
      }
      // Eine echte Upload-Segment-Antwort hat ccs == 0. Eine verspaetete
      // Initiate-Antwort (ccs == 2) wuerde sonst als "letztes Segment"
      // missgedeutet und ihre Kopfbytes landeten im String.
      if ((cs & 0xe0) !== 0x00) return;
      const respToggle = (cs & 0x10) !== 0;
      if (respToggle !== toggleBit) return; // veraltet/doppelt
      const n = 7 - ((cs >> 1) & 0x07);
      const last = (cs & 0x01) !== 0;
      for (let i = 0; i < n && i < 7; i += 1) segmentBuffer.push(data[1 + i] ?? 0);
      if (last) {
        storeIdentityResult(segmentBuffer);
        segmentBuffer = [];
        awaitingSegment = false;
        advancePhaseAfterResult();
      } else {
        toggleBit = !toggleBit;
        send(segmentRequest(nodeId, toggleBit));
      }
      return;
    }

    // Initiate-Antworten spiegeln Index/Subindex zurueck — ohne diese Pruefung
    // erzeugt fremder Verkehr im Bereich 0x581..0x5FF falsche Treffer.
    if (dlc < 8) return;
    const respIndex = (data[1] ?? 0) | ((data[2] ?? 0) << 8);
    if (respIndex !== pendingIndex || (data[3] ?? 0) !== 0) return;

    if (cs === 0x80) {
      // Abort: fuer die Praesenz trotzdem ein Beweis, fuer Identitaetsfelder
      // heisst es nur "nicht vorhanden" -> Feld bleibt leer.
      advancePhaseAfterResult();
      return;
    }
    if ((cs & 0xe0) !== 0x40) return;

    if (currentPhase === 'presence') {
      advancePhaseAfterResult();
      return;
    }

    const expedited = (cs & 0x02) !== 0;
    const sizeIndicated = (cs & 0x01) !== 0;
    if (expedited) {
      const raw = sizeIndicated ? 4 - ((cs >> 2) & 0x03) : 4;
      const n = Math.min(Math.max(raw, 0), 4);
      storeIdentityResult(Array.from(data.subarray(4, 4 + n)));
      advancePhaseAfterResult();
    } else {
      toggleBit = false;
      segmentBuffer = [];
      awaitingSegment = true;
      send(segmentRequest(nodeId, toggleBit));
    }
  }

  function startScan(): void {
    found.length = 0;
    renderRows();
    foundCount = 0;
    lastStopReason = '';
    scanning = true;
    currentNodeId = 0; // moveToNextNode() beginnt bei 1
    currentNode = emptyNode();
    startStopBtn.textContent = 'Stop Scan';
    if (tickTimer === null) tickTimer = setInterval(onTick, TICK_INTERVAL_MS);
    moveToNextNode();
  }

  function stopScan(reason: string): void {
    scanning = false;
    if (tickTimer !== null) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
    lastStopReason = reason;
    startStopBtn.textContent = 'Start Scan';
    updateStatus();
  }

  function toggleScan(): void {
    if (scanning) stopScan('stopped by user');
    else startScan();
  }

  function clearScan(): void {
    found.length = 0;
    foundCount = 0;
    lastStopReason = '';
    renderRows();
    updateStatus();
  }

  connection.on('rx-batch', (event) => {
    if (!scanning) return;
    const count = Math.min(event.count, frameCount(event.frames));
    for (let i = 0; i < count; i += 1) {
      const frame = readFrameRecord(event.frames, i);
      if ((frame.flags & FRAME_FLAG.extended) !== 0) continue;
      if (frame.id < 0x581 || frame.id > 0x5ff) continue;
      onSdoResponse(frame.id - 0x580, frame.dlc, frame.data);
    }
  });

  store.on('connection:changed', (event) => {
    if (scanning && event.state === 'disconnected') stopScan('connection lost');
  });

  return {
    show(): void {
      panel.show();
      updateStatus();
    },
  };
}
